import { createAsyncThunk } from '@reduxjs/toolkit';

import { ApiResListsType, OhsApiRequestName } from 'global-services/api/OhsApiModels';
import globalModuleSearch from 'search/OhsSearchServices';
import OhsUserLocalServices from 'user/OhsUserLocalServices';
import { OhsUser } from 'user/OhsUser';
import {
  OhsWmmSysFieldListsFilterPayload,
  OhsWmmSysFieldRootState,
} from 'wmm/models/OhsWmmSysFieldModels';
import { getWmmSysFieldLists } from 'wmm/OhsWmmSysFieldServices';
import OhsWmmSysFieldRecord from 'wmm/models/OhsWmmSysFieldRecord';

export const sysFieldSearch = async (
  searchDetails: any,
  apiRequestName?: OhsApiRequestName
): Promise<ApiResListsType<any[]> | null> => {
  const globalSearchRes: any = await globalModuleSearch(searchDetails, apiRequestName);
  return globalSearchRes.result;
};

export const sysFieldListsRPC = async (
  sysFieldState: OhsWmmSysFieldRootState,
  sysFieldFilters: OhsWmmSysFieldListsFilterPayload,
  count: boolean
): Promise<ApiResListsType<OhsWmmSysFieldRecord[]> | null> => {
  const { globalSearch } = sysFieldState;
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const userTier = user?.tierNum ?? 0;
  const hasGlobalSearch = sysFieldState.globalSearch.searchInfo.searchKey !== '';

  const getSearchFilter = () => {
    const { filter } = globalSearch.searchInfo;
    return { ...globalSearch.searchInfo, filter: { ...filter, count } };
  };

  if (userTier && userTier === 2) {
    const response = hasGlobalSearch
      ? await sysFieldSearch(getSearchFilter())
      : await getWmmSysFieldLists(sysFieldFilters);
    return response;
  }
  return null;
};

const sysFieldFilterOptions = (
  state: OhsWmmSysFieldRootState
): Partial<OhsWmmSysFieldListsFilterPayload> => {
  const { globalfilter } = state;
  const filter = globalfilter.filterInfo;
  return {
    viewPreset: 'view_1',
    archived: filter.sysFieldModule.archived,
  };
};

export const fetchSysFieldListsCountAsync = createAsyncThunk<
  ApiResListsType<OhsWmmSysFieldRecord[]> | null,
  undefined
>('sysField/fetchSysFieldListsCount', async (_, thunkAPI) => {
  const {
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsWmmSysFieldRootState;
  const sysFieldState = thunkAPI.getState() as OhsWmmSysFieldRootState;
  const sysFieldFiltersWithCount: OhsWmmSysFieldListsFilterPayload = {
    ...sysFieldFilterOptions(sysFieldState),
    count: true,
    page: 1,
    sort: { ...JSON.parse(String(filterInfo.sysFieldModule.sort)) },
  };

  try {
    const response = sysFieldListsRPC(sysFieldState, sysFieldFiltersWithCount, true);
    return await response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const fetchSysFieldListsAsync = createAsyncThunk<
  ApiResListsType<OhsWmmSysFieldRecord[]> | null,
  undefined
>('sysField/fetchSysFieldLists', async (_, thunkAPI) => {
  const {
    sysField,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsWmmSysFieldRootState;

  const sysFieldState = thunkAPI.getState() as OhsWmmSysFieldRootState;
  const data = sysField.sysFieldLists;
  const { currentPage } = sysField;
  const genSysFieldFilters: OhsWmmSysFieldListsFilterPayload = {
    ...sysFieldFilterOptions(sysFieldState),
    next: currentPage > 10 ? data?.pagination.next ?? '' : undefined,
    // remove page if it is greater than 10
    ...(currentPage <= 10 && { page: currentPage }),
    sort: { ...JSON.parse(String(filterInfo.sysFieldModule.sort)) },
    count: false,
  };

  try {
    const response = sysFieldListsRPC(sysFieldState, genSysFieldFilters, false);
    return await response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});
